import { useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

export function PromoSignupModal({ promo, onClose }) {
  const f = promo.form || {};
  const customFields = (f.custom_fields || []).filter((c) => c.enabled !== false);
  const [type, setType] = useState("Singles");
  const [form, setForm] = useState({ name: "", fargo: "", phone: "", email: "", notes: "", team_name: "" });
  const [extras, setExtras] = useState({});
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setExtra = (label, v) => setExtras((p) => ({ ...p, [label]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.show_name !== false && !form.name.trim()) { toast.error("Please enter your name."); return; }
    if (f.show_email !== false && !form.email.trim()) { toast.error("Please enter your email."); return; }
    for (const c of customFields) {
      if (c.required && !(extras[c.label] || "").trim()) { toast.error(`Please fill in "${c.label}".`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/promos/${promo.id}/signup`, {
        promo_id: promo.id,
        signup_type: f.show_type !== false ? type : "",
        team_name: f.show_team_name !== false && type === "Team" ? form.team_name : "",
        name: form.name,
        fargo: form.fargo,
        phone: form.phone,
        email: form.email,
        notes: form.notes,
        extras,
        company,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="promo-signup-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full my-8 p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="promo-signup-close" className="absolute top-4 right-4 text-[#999] hover:text-[#111]"><X className="h-6 w-6" /></button>
        {done ? (
          <div className="text-center py-6" data-testid="promo-signup-success">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-3" />
            <h3 className="font-heading font-bold text-xl">Sign-up received!</h3>
            <p className="text-sm text-[#555] mt-2">Thanks — we've got your details and will be in touch shortly.</p>
            <button onClick={onClose} className="mt-5 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c]">Close</button>
          </div>
        ) : (
          <>
            <h3 className="font-heading font-extrabold text-2xl leading-tight pr-8">{f.title || "Sign Up"}</h3>
            <p className="text-sm text-[#1E63C4] font-semibold mt-1">{promo.title}</p>
            {f.intro && <p className="text-sm text-[#555] mt-2 leading-relaxed">{f.intro}</p>}
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

              {f.show_type !== false && (
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">Sign-up type</label>
                  <div className="flex gap-2">
                    {["Singles", "Team"].map((t) => (
                      <button type="button" key={t} onClick={() => setType(t)} data-testid={`promo-signup-type-${t.toLowerCase()}`}
                        className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${type === t ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4]" : "border-[#E5E7EB] text-[#555]"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {f.show_team_name !== false && f.show_type !== false && type === "Team" && (
                <input className={inputCls} placeholder="Team name" value={form.team_name} onChange={(e) => setField("team_name", e.target.value)} data-testid="promo-signup-teamname" />
              )}

              {f.show_name !== false && (
                <input className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="promo-signup-name" />
              )}
              {f.show_fargo !== false && (
                <input className={inputCls} placeholder="Fargo rating (optional)" value={form.fargo} onChange={(e) => setField("fargo", e.target.value)} data-testid="promo-signup-fargo" />
              )}
              {f.show_phone !== false && (
                <input className={inputCls} placeholder="Contact number" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="promo-signup-phone" />
              )}
              {f.show_email !== false && (
                <input type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="promo-signup-email" />
              )}

              {customFields.map((c, ci) => (
                <div key={c.id || ci}>
                  <label className="block text-xs font-semibold text-[#555] mb-1">{c.label}{c.required && " *"}</label>
                  {c.type === "select" ? (
                    <select className={inputCls} value={extras[c.label] || ""} onChange={(e) => setExtra(c.label, e.target.value)} data-testid={`promo-signup-custom-${ci}`}>
                      <option value="">Select…</option>
                      {(c.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className={inputCls} value={extras[c.label] || ""} onChange={(e) => setExtra(c.label, e.target.value)} data-testid={`promo-signup-custom-${ci}`} />
                  )}
                </div>
              ))}

              {f.show_notes !== false && f.show_notes === true && (
                <textarea className={inputCls + " resize-y"} rows={3} placeholder="Notes / message (optional)" value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="promo-signup-notes" />
              )}

              <button type="submit" disabled={busy} data-testid="promo-signup-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {f.submit_label || "Submit sign-up"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
